import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
  type SerializedError,
} from '@reduxjs/toolkit';
import type { AuthUser } from '@augment-toolkit/auth-client';
import { authClient } from '../auth/authClient';

// `unknown` is the state before the app has asked whether a session exists, and
// it is distinct from `unauthenticated` on purpose: the guard must not redirect
// to login while the answer is still outstanding, or a reload of a protected
// page bounces the user through login every time.
export type AuthStatus = 'unknown' | 'pending' | 'authenticated' | 'unauthenticated';

export type AuthState = {
  status: AuthStatus;
  user: AuthUser | null;
  /** Set when a login attempt failed. Shown on the login page. */
  error: string | null;
  /** Path the user was trying to reach, carried through the provider round trip. */
  returnTo: string | null;
};

const initialState: AuthState = {
  status: 'unknown',
  user: null,
  error: null,
  returnTo: null,
};

// **RTK serialises a thrown error before it reaches a reducer**, so `action.error`
// is a plain `SerializedError` object and never an `Error` instance. An
// `instanceof Error` check here is always false, which shows the generic
// fallback for every failure and hides what actually went wrong.
const message = (error: SerializedError): string => error.message ?? 'Authentication failed.';

// Is there already a session? Called once on boot, before anything is rendered
// behind the guard.
export const restoreSession = createAsyncThunk('auth/restoreSession', () => authClient.getUser());

// Leaves the page in the happy path, so nothing after this resolves.
export const beginLogin = createAsyncThunk('auth/beginLogin', async (returnTo?: string) => {
  await authClient.login(returnTo);
});

/**
 * **The reason a callback failed is logged, not shown.** `oidc-client-ts` throws
 * its own internal strings — "No matching state found in storage" is what a
 * *reload of the callback URL* produces — which mean nothing to anyone signing
 * in, and every one of these failures has the same recovery: start again.
 */
export const completeLogin = createAsyncThunk(
  'auth/completeLogin',
  async () => {
    try {
      return await authClient.handleCallback();
    } catch (error) {
      console.error('The sign-in callback failed.', error);
      throw error;
    }
  },
  {
    // **Load-bearing under StrictMode.** React mounts, unmounts and remounts in
    // development, so the callback effect fires twice — and the second exchange
    // fails, because an authorization code is single use. The `pending` action
    // dispatches synchronously, so the second attempt sees it and is skipped.
    condition: (_arg, { getState }) => {
      const { auth } = getState() as { auth: AuthState };
      return auth.status !== 'pending';
    },
  },
);

export const signOut = createAsyncThunk('auth/signOut', async () => {
  await authClient.logout();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Silent renew failed or the token expired: the session is over even though
    // the app is still open. Dispatched from the auth client, not from a view.
    sessionEnded: (state) => {
      state.status = 'unauthenticated';
      state.user = null;
      state.returnTo = null;
      state.error = 'Your session has expired. Please sign in again.';
    },
    errorDismissed: (state) => {
      state.error = null;
    },
    returnToRecorded: (state, action: PayloadAction<string>) => {
      state.returnTo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.status = action.payload === null ? 'unauthenticated' : 'authenticated';
        state.user = action.payload;
      })
      // A failure to *read* an existing session means there is no usable
      // session, which is the same outcome as not having one. Surfacing it would
      // put a scary message on a cold start.
      .addCase(restoreSession.rejected, (state) => {
        state.status = 'unauthenticated';
        state.user = null;
      })

      .addCase(beginLogin.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      // Only reached if the redirect itself failed; otherwise the page is gone.
      .addCase(beginLogin.rejected, (state, action) => {
        state.status = 'unauthenticated';
        state.error = message(action.error);
      })

      .addCase(completeLogin.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(completeLogin.fulfilled, (state, action) => {
        state.status = action.payload === null ? 'unauthenticated' : 'authenticated';
        state.user = action.payload;
        state.returnTo = action.payload?.returnTo ?? null;
      })
      // **One sentence for every way this fails, and it is not the library's.**
      // Expired code, reused code, reloaded callback, storage cleared mid-flow —
      // the cause differs and the fix never does. Real reason in the console.
      .addCase(completeLogin.rejected, (state) => {
        state.status = 'unauthenticated';
        state.user = null;
        state.error =
          'That sign-in could not be completed. It may have expired or already been used — ' +
          'please sign in again.';
      })

      .addCase(signOut.fulfilled, (state) => {
        state.status = 'unauthenticated';
        state.user = null;
        state.returnTo = null;
        state.error = null;
      })
      // Logout clears the local session before redirecting, so a failure here
      // still means signed out locally — say so rather than leaving the UI
      // claiming a session that does not work.
      .addCase(signOut.rejected, (state, action) => {
        state.status = 'unauthenticated';
        state.user = null;
        state.error = message(action.error);
      });
  },
});

export const { sessionEnded, errorDismissed, returnToRecorded } = authSlice.actions;
export const authReducer = authSlice.reducer;
